import React, { Suspense, lazy, useCallback, useState } from "react";

import {
  ClientReport,
  downloadReportFile,
  useReportFile,
} from "apps/company/api/rest/company-dashboard-reports";
import { useDashboardTiles } from "apps/company/api/rest/company-dashboard-tiles";
import { useTranslation } from "lib/i18n/use-translation";
import { logError } from "utility/Function";

import { loadReportPages } from "./load-report-pages";
import { reportFileName } from "./report-file-name";
import styles from "./reports-library.module.scss";

const K = "admin:companyDashboard.reportsLibrary.viewer";

type BoundaryProps = {
  onError: (error: unknown) => void;
  children: React.ReactNode;
};

class PreviewBoundary extends React.Component<
  BoundaryProps,
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: unknown) {
    this.props.onError(error);
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}

type Props = {
  report: ClientReport;
  period: string;
  onOpened?: (report: ClientReport) => void;
  onDownloaded?: (report: ClientReport) => void;
};

const iconProps = {
  "aria-hidden": true,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.6,
  strokeLinecap: "round",
  strokeLinejoin: "round",
} as const;

const OpenIcon = () => (
  <svg {...iconProps} width={16} height={16}>
    <path d="M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
  </svg>
);

const DownloadIcon = () => (
  <svg {...iconProps} width={16} height={16}>
    <path d="M12 4v11M7 10l5 5 5-5M5 20h14" />
  </svg>
);

const FileErrorIcon = () => (
  <svg {...iconProps} width={36} height={36} strokeWidth={1.5}>
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
    <path d="M14 3v5h5M12 12v3M12 18h.01" />
  </svg>
);

const ReportViewer = ({ report, period, onOpened, onDownloaded }: Props) => {
  const { t } = useTranslation(["admin"]);
  const tiles = useDashboardTiles();
  const { file, retry } = useReportFile(report.id);
  const [downloadFailed, setDownloadFailed] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  // React.lazy caches a failed import, so each failure gets a fresh one
  // for Try again to load.
  const [ReportPages, setReportPages] = useState(() => lazy(loadReportPages));

  const readyUrl = file.state === "ready" ? file.url : null;
  const failed =
    file.state === "error" || (readyUrl !== null && failedUrl === readyUrl);

  const previewFailed = useCallback(
    (error: unknown) => {
      setFailedUrl(readyUrl);
      setReportPages(() => lazy(loadReportPages));
      logError("Company dashboard report preview failed", error);
    },
    [readyUrl]
  );

  const loading = (
    <p role="status" className={styles["frame-status"]}>
      {t(`${K}.loading`)}
    </p>
  );

  const pageLabel = useCallback(
    (page: number, total: number) => t(`${K}.page`, { page, total }),
    [t]
  );

  const company = "companyName" in tiles ? tiles.companyName : null;
  const heading = company ? t(`${K}.header`, { period, company }) : period;

  const open = () => {
    if (file.state !== "ready") return;
    window.open(file.url, "_blank", "noopener");
    onOpened?.(report);
  };

  const download = () => {
    setDownloadFailed(false);
    setDownloading(true);
    downloadReportFile(report.id, reportFileName(report.title))
      .then(() => onDownloaded?.(report))
      .catch((error) => {
        setDownloadFailed(true);
        logError("Company dashboard report download failed", error);
      })
      .finally(() => setDownloading(false));
  };

  return (
    <>
      <div className={styles["panel-header"]}>
        <div>
          <p className={styles["eyebrow"]}>{heading}</p>
          <h3 className={styles["panel-title"]}>{report.title}</h3>
        </div>
        <div className={styles["actions"]}>
          <button
            type="button"
            className={styles["action"]}
            onClick={open}
            disabled={file.state !== "ready"}
            aria-label={t(`${K}.openNewTab`)}
          >
            <OpenIcon />
            {t(`${K}.open`)}
          </button>
          <button
            type="button"
            className={styles["action"]}
            onClick={download}
            disabled={downloading}
          >
            <DownloadIcon />
            {t(`${K}.download`)}
          </button>
        </div>
      </div>

      {downloadFailed && (
        <p role="alert" className={styles["download-error"]}>
          {t(`${K}.downloadError`)}
        </p>
      )}

      {file.state === "loading" && loading}

      {failed && (
        <div role="alert" className={styles["frame-error"]}>
          <FileErrorIcon />
          <p className={styles["frame-error-title"]}>{t(`${K}.error`)}</p>
          <p className={styles["frame-error-body"]}>{t(`${K}.errorBody`)}</p>
          <div className={styles["frame-error-actions"]}>
            <button type="button" className={styles["action"]} onClick={retry}>
              {t("admin:companyDashboard.reportsLibrary.retry")}
            </button>
            <button
              type="button"
              className={styles["primary"]}
              onClick={download}
              disabled={downloading}
            >
              {t(`${K}.download`)}
            </button>
          </div>
        </div>
      )}

      {readyUrl !== null && !failed && (
        <PreviewBoundary onError={previewFailed}>
          <Suspense fallback={loading}>
            <ReportPages
              url={readyUrl}
              label={t(`${K}.frameTitle`, { title: report.title })}
              loadingText={t(`${K}.loading`)}
              pageLabel={pageLabel}
              onFailed={previewFailed}
            />
          </Suspense>
        </PreviewBoundary>
      )}

      {!failed && (
        <button
          type="button"
          className={styles["primary"]}
          onClick={download}
          disabled={downloading}
        >
          <DownloadIcon />
          {t(`${K}.downloadFull`)}
        </button>
      )}
    </>
  );
};

export default ReportViewer;
